import { useCallback, useEffect, useMemo, useState } from "react";
import { TbPencil } from "react-icons/tb";
import { FormattedMessage } from "react-intl";
import { connect, useDispatch, useSelector } from "react-redux";
import { post } from "../../../api";
import { actionMergeSetting } from "../../../redux/settings/settingsAction";
import { buildRefLink } from "../../../tools/functions";
import Input from "../../cmp/form/Input";
import useRefLink from "../../cmp/hooks/useRefLink";
import useToggle from "../../cmp/hooks/useToggle";
import Modal from "../../cmp/Modal";
import RenderCommission from "../../cmp/RenderCommission";
import ShareBarWithModal from "../../cmp/ShareBar";

function ReferralLink({ status, commission_type, commission_value }) {
  const [ref_change_allowed, multi_ref_codes] = useSelector((state) => [
    state.settings.ref_change_allowed,
    state.settings.multi_ref_codes,
  ]);
  const [ref_link] = useRefLink(null, true);
  return (
    <div className={"mb-4"}>
      <h4>
        <FormattedMessage
          id={"referralLink"}
          defaultMessage={"Referral Link"}
        />
        {Number(commission_value) > 0 ? (
          <span className="ml-2 badge badge-secondary referralLinkBadge">
            <FormattedMessage
              id={"referralLinkBadge"}
              values={{
                commission_value: (
                  <RenderCommission
                    commission_type={commission_type}
                    commission_value={commission_value}
                  />
                ),
              }}
            />
          </span>
        ) : null}
      </h4>
      <p className={"text-muted"}>
        <FormattedMessage
          id={"referralLinkDesc"}
          values={{
            commission_value: (
              <RenderCommission
                commission_type={commission_type}
                commission_value={commission_value}
              />
            ),
          }}
        />
      </p>

      {["approved", "invited"].indexOf(status) > -1 && (
        <div>
          {
            <p>
              <a
                style={{ whiteSpace: "pre-line", wordBreak: "break-all" }}
                rel={"noopener noreferrer nofollow"}
                href={ref_link}
                target={"_blank"}
              >
                {decodeURI(ref_link)}
              </a>
            </p>
          }
          <div className={"d-flex align-items-center "}>
            <ShareBarWithModal link={ref_link} />
            {ref_change_allowed && !multi_ref_codes ? (
              <ReferralLinkEditor />
            ) : null}
          </div>
          <RenderPrimaryQuickLinks />
        </div>
      )}
      {status === "pending" && (
        <p className={"alert alert-warning"}>
          <FormattedMessage id={"Account under review"} />
        </p>
      )}
    </div>
  );
}

function RenderPrimaryQuickLinks() {
  const quick_links = useSelector((state) => {
    const ql = state.settings.quick_links;
    if (ql) {
      return ql.filter(({ isPrimary }) => isPrimary);
    }
  });
  if (!quick_links || quick_links.length === 0) {
    return null;
  }

  return (
    <div>
      <>
        {quick_links.map((item) => {
          return (
            <RenderLink
              is_redirect={item.is_redirect ?? false}
              key={item.id}
              title={item.title}
              url={item.url}
            />
          );
        })}
      </>
    </div>
  );
}

function RenderLink({ title, url, is_redirect }) {
  const [ref_link] = useRefLink(url);
  return (
    <>
      <hr />
      {
        <p>
          <a
            style={{ whiteSpace: "pre-line", wordBreak: "break-all" }}
            rel={"noopener noreferrer nofollow"}
            href={is_redirect ? url : ref_link}
            title={title}
            target={"_blank"}
          >
            {decodeURI(is_redirect ? url : ref_link)}
          </a>
        </p>
      }
      <div className={"d-flex align-items-center "}>
        <ShareBarWithModal link={is_redirect ? url : ref_link} />
      </div>
    </>
  );
}

const replacer = /[^A-z0-9~_.-]/gi;
function ReferralLinkEditor() {
  const [website, ref_code, ref_identifiers] = useSelector((state) => [
    state.settings.website,
    state.settings.ref_code,
    state.settings.ref_identifiers,
  ]);

  const w = useMemo(() => {
    return website && website.startsWith("https")
      ? website
      : `https://${website}`;
  }, [website]);
  const [refCode, setRefCode] = useState("");
  useEffect(() => {
    setRefCode(ref_code);
  }, [ref_code]);
  const [visible, toggle] = useToggle(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const setRef = useCallback((e) => {
    setRefCode(e.target.value.replace(replacer, "").trim());
  }, []);
  const codes = useMemo(() => {
    return refCode
      .split("\n")
      .map((i) => i.trim())
      .filter((i) => i);
  }, [refCode]);
  const dispatch = useDispatch();
  const onSubmit = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      setSaving(true);
      post("/referral_codes", {
        referral_codes: codes,
      })
        .then((data) => {
          setSaving(false);
          if (data.codes) {
            dispatch(
              actionMergeSetting({
                primary_ref_link: data.primary_ref_link || undefined,
                is_redirect: data.is_redirect || undefined,
                ref_code: data.codes[0],
              }),
            );
            toggle();
          } else if (data.conflicts) {
            setError("Referral code is already in use");
          } else {
            setError("Network error");
          }
        })
        .catch((e) => {
          setSaving(false);
        });
    },
    [codes, dispatch, toggle],
  );
  return (
    <>
      <button
        onClick={toggle}
        title="Edit Referral Link"
        className="btn btn-link"
      >
        <TbPencil style={{ width: 20, height: 20 }} />
      </button>
      <Modal
        confirmLoading={saving}
        disabled={refCode === ref_code || !codes || codes.length === 0}
        visible={visible}
        okText={<FormattedMessage id={"Submit"} />}
        onSubmit={onSubmit}
        onCancel={toggle}
        title={<FormattedMessage id={"referralCode"} />}
      >
        <p>
          <FormattedMessage id={"referralCodeDesc"} />
        </p>

        <Input
          prefix={w + "/?ref="}
          className={"form-control"}
          value={refCode}
          onChange={setRefCode}
        />
        {error ? <p className="alert alert-danger">{error}</p> : null}
        <small className="text-primary">
          {buildRefLink(refCode, w, ref_identifiers)}
        </small>
      </Modal>
    </>
  );
}

function mapStateToProps(state) {
  const { standard } = state.commissions;
  return standard || {};
}

export default connect(mapStateToProps)(ReferralLink);
